// A lazy file failed to download - reload once and try again.
//
// The Stewards' city, the Frontier's island and the TV's world are separate
// files fetched the first time a screen opens one (three.js is big, and a
// phone holding neither role never needs it). When that fetch fails, Vite
// fires `vite:preloadError` on window and then throws, and the board never
// draws. Two ways in:
//  - a tab left open across a deploy: `--delete` in the S3 sync removes the
//    old hashed files, and CloudFront answers the missing one with the HTML
//    page - Sentry caught exactly that 2026-10-16, a Safari tab opening the
//    Stewards' board for the first time after a deploy;
//  - a network blip (weak phone signal, a phone waking the tab).
// A reload fixes both, and rejoinRoom puts the player back at the table.
// The last attempt's time sits in sessionStorage so a load that keeps
// failing reloads once, then lets the error throw (and reach Sentry)
// instead of looping. Ported from Full Table's copy.

const KEY = 'preload-error-reload-at';
export const RETRY_WINDOW_MS = 60 * 1000;

/** Should this failure trigger a reload? Records the attempt if so. */
export function shouldReloadForPreloadError(storage = window.sessionStorage, now = Date.now()) {
  try {
    const last = Number(storage.getItem(KEY)) || 0;
    if (now - last < RETRY_WINDOW_MS) return false;
    storage.setItem(KEY, String(now));
    return true;
  } catch {
    // No storage means no loop guard; let the error surface instead.
    return false;
  }
}

export function watchPreloadErrors(target = window, reload = () => window.location.reload()) {
  target.addEventListener('vite:preloadError', (event) => {
    if (!shouldReloadForPreloadError()) return;
    event.preventDefault(); // don't throw: the page is about to be replaced
    reload();
  });
}

// Once the reload above has cancelled a failed lazy file, Vite's import()
// RESOLVES with undefined instead of throwing - and the code after it runs
// in the moment before the page is replaced. Sentry caught the Frontier's
// island destructuring that nothing (2026-10-21, a phone open across a
// deploy). A board that loads a lazy file goes through this and simply
// stops when it gets null: the reload is already on its way.
export async function loadLazy(...loads) {
  const modules = await Promise.all(loads.map((load) => load()));
  return modules.every(Boolean) ? modules : null;
}
